import { applicationViewSchema, type ApplicationView } from '@growup/accounts-shared';
import { Button, Card, Field, Input, Notice, Select } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { APPLICATION_STATUS_LABEL } from './labels';
import { ProfileFields } from './Profile';

type Status = ApplicationView['status'];

/** Member applications (RN-A6): approve or reject, with an internal note. */
export function ApplicationsView({ onOpenMember }: { onOpenMember: (accountId: string) => void }) {
  const [status, setStatus] = useState<Status>('pending');
  const [list, setList] = useState<ApplicationView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    getJson(`/api/members/applications?status=${status}`, z.array(applicationViewSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [status]);

  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Estado" className="w-48">
          <Select value={status} onChange={(e) => setStatus(e.target.value as Status)}>
            {(Object.keys(APPLICATION_STATUS_LABEL) as Status[]).map((s) => (
              <option key={s} value={s}>
                {APPLICATION_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant="secondary" size="sm" className="mb-1.5" onClick={load}>
          Atualizar
        </Button>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {list && list.length === 0 ? (
        <p className="text-gu-ink-muted">
          {status === 'pending' ? 'Não há pedidos à espera.' : 'Nenhum pedido neste estado.'}
        </p>
      ) : null}
      {list?.map((application) => (
        <ApplicationCard
          key={application.id}
          application={application}
          onDecided={load}
          onOpenMember={onOpenMember}
        />
      ))}
    </div>
  );
}

export function ApplicationCard({
  application,
  onDecided,
  onOpenMember,
}: {
  application: ApplicationView;
  onDecided: () => void;
  onOpenMember: (accountId: string) => void;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { member } = application;

  const decide = async (action: 'approve' | 'reject') => {
    setError(null);
    setBusy(true);
    try {
      await send('POST', `/api/members/applications/${application.id}/${action}`, {
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDecided();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      headingLevel={2}
      eyebrow={`Pedido de ${formatDateTime(application.submittedAt)}`}
      title={member.profile?.fullName ?? member.login}
      actions={
        <Button variant="ghost" size="sm" onClick={() => onOpenMember(member.accountId)}>
          Abrir ficha
        </Button>
      }
    >
      <p className="text-gu-ink-muted">
        Login {member.login}
        {application.decidedAt
          ? ` · ${APPLICATION_STATUS_LABEL[application.status].toLowerCase()} a ${formatDateTime(application.decidedAt)}`
          : ''}
      </p>
      {member.profile ? (
        <ProfileFields profile={member.profile} />
      ) : (
        <p className="text-gu-ink-muted">Sem permissão para ver os dados desta pessoa.</p>
      )}
      {application.decisionNote ? <p>Nota: {application.decisionNote}</p> : null}
      {application.status === 'pending' ? (
        <>
          <Field label="Nota interna" hint="Não escreva dados pessoais aqui." optional>
            <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <div className="flex gap-3">
            <Button size="sm" disabled={busy} onClick={() => void decide('approve')}>
              ✓ Aprovar
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onClick={() => void decide('reject')}
            >
              ✕ Recusar
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}
